import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useModal } from '../../../../hooks/useModal'
import { useDeleteBookingAllowed } from './useDeleteBookingAllowed'
import { DeleteBookingConfirmationModal } from '../DeleteBookingConfirmationModal'
import { FacilityBookingListItemInner } from './FacilityBookingListItemInner'
import { IBookingModel } from '../../../../interfaces/models/bookings.interfaces'
import { IFacilityModel } from '../../../../interfaces/models/facilities.interfaces'
import { societyQueries } from '../../../../api/societies'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { bookingQueries, deleteBooking } from '../../../../api/bookings'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'

export interface FacilityBookingListItem {
  booking: IBookingModel
  facility: IFacilityModel
}

export const FacilityBookingListItem = ({
  booking,
  facility,
}: FacilityBookingListItem): React.ReactNode => {
  const { data: society } = useQuery(
    societyQueries.getSociety(booking.societyId)
  )
  const isMobile = useIsMobile()
  const {
    show: showDeleteBookingModal,
    open: openDeleteBookingModal,
    close: closeDeleteBookingModal,
  } = useModal()

  const { allowed: deleteBookingAllowed, otherUsersOwnsBooking } =
    useDeleteBookingAllowed(booking, society)

  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const client = useQueryClient()
  const { mutate: mutateDeleteBooking } = useMutation({
    mutationFn: deleteBooking,
    onSuccess: async () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('facilityView.bookings.flashMessage.deleteBookingSuccess')
      )
      await client.invalidateQueries({ queryKey: [bookingQueries.scope] })
      closeDeleteBookingModal()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('facilityView.bookings.flashMessage.deleteBookingFailure')
      )
    },
  })
  const onDeleteBooking = async (): Promise<void> => {
    booking && mutateDeleteBooking(booking._id)
  }

  return (
    <>
      {isMobile ? (
        <button
          className="w-full"
          disabled={!deleteBookingAllowed}
          onClick={deleteBookingAllowed ? openDeleteBookingModal : undefined}
        >
          <FacilityBookingListItemInner
            booking={booking}
            facility={facility}
            openDeleteBookingModal={openDeleteBookingModal}
            deleteBookingAllowed={deleteBookingAllowed}
          />
        </button>
      ) : (
        <FacilityBookingListItemInner
          booking={booking}
          facility={facility}
          openDeleteBookingModal={openDeleteBookingModal}
          deleteBookingAllowed={deleteBookingAllowed}
        />
      )}
      <DeleteBookingConfirmationModal
        onDelete={onDeleteBooking}
        showModal={showDeleteBookingModal}
        closeModal={closeDeleteBookingModal}
        disableCloseOnConfirm
        booking={booking}
        otherUsersOwnsBooking={otherUsersOwnsBooking}
      />
    </>
  )
}
